import React, { useReducer, useState } from 'react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, TextField } from '@mui/material';
import GoogleAutocomplete from '../../../components/googleautocomplete/googleautocomplete.component';
import { CustomDateTimePicker } from '../../../components/datetimepicker';
import dayjs from 'dayjs';
import Grid from '@mui/material/Grid2';
import useFetchTests from './hooks/useFetchTests';
import httpClient from '../../../services/http';
import { APIUtils } from '../../../utils/apiutils';
import PropTypes from 'prop-types';
import Spinner from '../../../components/spinner/spinner';
import Swal from 'sweetalert2';
import { CUSTOM_EVENTS } from '../../../utils/constants';

const SaveCenterBtn = ({centerData, closeDialog}) => {
    const [loading, setLoading] = useState(false);

    const isValid = () => {
        if (!centerData.name) {
            return false;
        }
        if (!centerData.location) {
            return false;
        }
        if (centerData.services.length < 1) {
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!isValid()) {
            return;
        }
        const payload = {...centerData, services: centerData.services.map(({id}) => id)};
        setLoading(true);
        const {err, res} = await httpClient.post(APIUtils.getAdminAPIEndpoint('add-test-center'), payload).then(res => ({err: null, res: res.data})).catch(err => ({err, res: null}));
        setLoading(false);
        if (err) {
            Swal.fire('Something Went Wrong!', err.cause.response.data.message, 'error');
            return;
        }

        document.dispatchEvent(new CustomEvent(CUSTOM_EVENTS.REFETCH_TEST_CENTERS));
        Swal.fire(res.message, '', 'success');
        closeDialog();
    };

    const saveBtn = () => {
        if (loading) {
            return <Button variant='contained' color='primary'>Saving&nbsp;<Spinner /></Button>;
        }
        return <Button variant='contained' color='primary' onClick={handleSave} disabled={!isValid()}>Save</Button>;
    };

    return (
        <FormControl >{saveBtn()}</FormControl>
    );
};
SaveCenterBtn.propTypes = {
    centerData: PropTypes.object.isRequired,
    closeDialog: PropTypes.func,
};

const Addcenter = () => {
    const CENTER_FORM_DATA = {
        name: '',
        location: '',
        coordinate: {lat: 0, lng: 0},
        openingTime: dayjs().hour(9).minute(0).second(0),
        closingTime: dayjs().hour(9).minute(0).second(0),
        // services: '',
        services: [],
    };
    const [open, setOpen] = useState(false);
    const [ centerData, updateCenterData ] = useReducer((prev, next) => {
        return {...prev, ...next};
    }, CENTER_FORM_DATA);
    const {tests} = useFetchTests();

    const handleDialog = (toggle) => () => {
        setOpen(toggle);
        if (!toggle) {
            updateCenterData({ ...CENTER_FORM_DATA });
        }
    };

    const handleCenterData = (e) => {
        const { name, value } = e.target;
        updateCenterData({ [name]: value });
    };

    const handleLocation = (locData) => {
        let loc = '';
        let coord = {lat: 0, lng: 0};
        if (locData) {
            const {formatted_address: formattedAddress, geometry: { location: { lat, lng } }} = locData;
            loc = formattedAddress;
            coord = { lat: lat(), lng: lng() };
        }
        updateCenterData({ location: loc, coordinate: coord });
    };

    const handleTime = (fieldName) => (timeData) => {
        updateCenterData({ [fieldName]: timeData });
    };

    const testsListSelect = () => {
        const autcompleteInput = (params) => (
            <TextField
                {...params}
                label="Services"
                placeholder="Services"
            />
        );
        const handleAutocompleteChange = (event, services) => {
            updateCenterData({ services });
        };
        return (
            <FormControl fullWidth size="small">
                <Autocomplete
                    multiple
                    id="center-services"
                    size="small"
                    options={tests}
                    getOptionLabel={(option) => option.servicename}
                    getOptionKey={(option) => option.id}
                    // defaultValue={[top100Films[13]]}
                    filterSelectedOptions
                    renderInput={autcompleteInput}
                    onChange={handleAutocompleteChange}
                />
            </FormControl>
        );
    };

    const timePickerProps = {sx: { width: '100%' }};
    const addCenterDialog = (
        <Dialog open={open} maxWidth='sm' fullWidth className='has-autocomplete'>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <IconButton onClick={handleDialog(false)} size='small'>
                    <CloseOutlinedIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Grid container className='profile-grid' spacing={3}>
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                        <TextField
                            name='name'
                            label='Center Name'
                            value={centerData.name}
                            fullWidth
                            size='small'
                            onChange={handleCenterData}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                        <GoogleAutocomplete
                            setAddress={handleLocation}
                            address={centerData.location}
                            textFieldProps={{ size: 'small', label: 'Center Location', placeholder: 'Center Location' }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                        <CustomDateTimePicker
                            timeOnly={true}
                            value={dayjs(centerData.openingTime)}
                            handleChange={handleTime('openingTime')}
                            pickerProps={{ label: 'Opening Time', name: 'openingTime', ...timePickerProps }}
                            className="small-time-picker-test"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                        <CustomDateTimePicker
                            timeOnly={true}
                            value={dayjs(centerData.closingTime)}
                            handleChange={handleTime('closingTime')}
                            pickerProps={{ label: 'Closing Time', name: 'closingTime', ...timePickerProps }}
                            className="small-time-picker-test"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                        {testsListSelect()}
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <DialogActions>
                <FormControl>
                    <Button variant='outlined' color='secondary' onClick={handleDialog(false)}>Cancel</Button>
                </FormControl>
                <SaveCenterBtn centerData={centerData} closeDialog={handleDialog(false)} />
            </DialogActions>
        </Dialog>
    );
    return (
        <>
            {addCenterDialog}
            <div className="centerContent">
                <Button
                    size='small'
                    variant='contained'
                    sx={{ mr: 1 }}
                    color='primary'
                    onClick={handleDialog(true)}
                    // onClick={() => setModalState(prevState => ({
                    //     ...prevState,
                    //     isEditMedicine: false,
                    //     openAddEditMedicine: true
                    // }))}
                >
                    Add Test Center
                </Button>
            </div>
        </>
    );
};

export default Addcenter;